import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  StarIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { toast } from 'sonner'
import Button from '../ui/Button'
import Card from '../ui/Card'
import ConfirmDialog from '../ui/ConfirmDialog'
import PageLoader from '../ui/PageLoader'
import {
  createPassNameMappingAdmin,
  deletePassNameMapping,
  listPassNameMappingsGrouped,
  promotePassNameMapping,
  updatePassNameMapping,
  type PassNameMappingGroup,
  type PassNameMappingItem
} from '../../utils/passNameMappingsClient'

function formatDate(iso: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

type EditModalProps = {
  open: boolean
  item: PassNameMappingItem | null
  onClose: () => void
  onSaved: () => void
}

function EditMappingModal({ open, item, onClose, onSaved }: EditModalProps) {
  const [canonicalPassName, setCanonicalPassName] = useState('')
  const [usageCount, setUsageCount] = useState('1')
  const [setPreferred, setSetPreferred] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || !item) return
    setCanonicalPassName(item.canonicalPassName)
    setUsageCount(String(item.usageCount))
    setSetPreferred(item.isPreferred)
  }, [open, item])

  if (!open || !item) return null

  const handleSave = async () => {
    const trimmed = canonicalPassName.trim()
    if (!trimmed) {
      toast.error('Canonical pass name is required.')
      return
    }
    const parsedCount = Number(usageCount)
    if (!Number.isInteger(parsedCount) || parsedCount < 0) {
      toast.error('Usage count must be a non-negative integer.')
      return
    }
    setBusy(true)
    try {
      await updatePassNameMapping(item.id, {
        canonicalPassName: trimmed,
        usageCount: parsedCount
      })
      if (setPreferred && !item.isPreferred) {
        await promotePassNameMapping(item.id)
      }
      toast.success('Pass mapping updated.')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update mapping.'
      toast.error(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-pass-mapping-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h3 id="edit-pass-mapping-title" className="text-base font-semibold text-gray-900">
          Edit pass mapping
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          Raw pass: <span className="font-medium text-gray-800">{item.rawPassName}</span>
        </p>
        <div className="mt-4 space-y-3">
          <label className="block text-sm text-gray-700">
            Canonical pass name
            <input
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={canonicalPassName}
              onChange={(e) => setCanonicalPassName(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="block text-sm text-gray-700">
            Usage count
            <input
              type="number"
              min={0}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={usageCount}
              onChange={(e) => setUsageCount(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={setPreferred}
              onChange={(e) => setSetPreferred(e.target.checked)}
              disabled={busy}
            />
            Set as preferred (active pre-fill)
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}

type AddModalProps = {
  open: boolean
  defaultRawPassName?: string
  onClose: () => void
  onSaved: () => void
}

function AddMappingModal({ open, defaultRawPassName, onClose, onSaved }: AddModalProps) {
  const [rawPassName, setRawPassName] = useState('')
  const [canonicalPassName, setCanonicalPassName] = useState('')
  const [setPreferred, setSetPreferred] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setRawPassName(defaultRawPassName ?? '')
    setCanonicalPassName('')
    setSetPreferred(true)
  }, [open, defaultRawPassName])

  if (!open) return null

  const handleCreate = async () => {
    const raw = rawPassName.trim()
    const canonical = canonicalPassName.trim()
    if (!raw || !canonical) {
      toast.error('Raw and canonical pass names are required.')
      return
    }
    setBusy(true)
    try {
      await createPassNameMappingAdmin({
        rawPassName: raw,
        canonicalPassName: canonical,
        setPreferred
      })
      toast.success('Pass mapping created.')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create mapping.'
      toast.error(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-pass-mapping-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h3 id="add-pass-mapping-title" className="text-base font-semibold text-gray-900">
          Add pass mapping
        </h3>
        <div className="mt-4 space-y-3">
          <label className="block text-sm text-gray-700">
            Raw pass name
            <input
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={rawPassName}
              onChange={(e) => setRawPassName(e.target.value)}
              disabled={busy || Boolean(defaultRawPassName)}
            />
          </label>
          <label className="block text-sm text-gray-700">
            Canonical pass name
            <input
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={canonicalPassName}
              onChange={(e) => setCanonicalPassName(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={setPreferred}
              onChange={(e) => setSetPreferred(e.target.checked)}
              disabled={busy}
            />
            Set as preferred (active pre-fill)
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleCreate()} disabled={busy}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function PassNameMappingsAdmin() {
  const [groups, setGroups] = useState<PassNameMappingGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [editItem, setEditItem] = useState<PassNameMappingItem | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [addRawDefault, setAddRawDefault] = useState<string | undefined>()
  const [deleteTarget, setDeleteTarget] = useState<PassNameMappingItem | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listPassNameMappingsGrouped({
        search: debouncedSearch || undefined
      })
      setGroups(res.groups)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load pass mappings.'
      toast.error(message)
      setGroups([])
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch])

  useEffect(() => {
    void load()
  }, [load])

  const totalMappings = useMemo(
    () => groups.reduce((sum, g) => sum + g.mappings.length, 0),
    [groups]
  )

  const toggleGroup = (rawPassName: string) => {
    setExpanded((prev) => ({ ...prev, [rawPassName]: !prev[rawPassName] }))
  }

  const handlePromote = async (item: PassNameMappingItem) => {
    try {
      await promotePassNameMapping(item.id)
      toast.success(`"${item.rawPassName}" now pre-fills as "${item.canonicalPassName}".`)
      await load()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to set preferred mapping.'
      toast.error(message)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleteBusy(true)
    try {
      await deletePassNameMapping(deleteTarget.id)
      toast.success('Pass mapping deleted.')
      setDeleteTarget(null)
      await load()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete mapping.'
      toast.error(message)
    } finally {
      setDeleteBusy(false)
    }
  }

  return (
    <Card title="Pass Name Mappings">
      <div className="space-y-4" data-testid="pass-name-mappings-admin">
        <p className="text-xs text-gray-500">
          Mappings learned from Rename Pass across all users. The active mapping pre-fills new
          uploads. Use Set as preferred to correct wrong crowd-learned suggestions.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            type="search"
            placeholder="Search raw or canonical pass…"
            className="block w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="pass-name-mappings-search"
          />
          <Button
            type="button"
            onClick={() => {
              setAddRawDefault(undefined)
              setAddOpen(true)
            }}
          >
            Add mapping
          </Button>
        </div>

        {loading ? (
          <PageLoader />
        ) : groups.length === 0 ? (
          <p className="text-sm text-gray-500">No pass mappings found.</p>
        ) : (
          <p className="text-xs text-gray-500">
            {groups.length} raw pass name{groups.length === 1 ? '' : 's'} · {totalMappings} mapping
            {totalMappings === 1 ? '' : 's'}
          </p>
        )}

        <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
          {groups.map((group) => {
            const isOpen = expanded[group.rawPassName] ?? true
            return (
              <li key={group.rawPassName} data-testid={`pass-mapping-group-${group.rawPassName}`}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
                  onClick={() => toggleGroup(group.rawPassName)}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {isOpen ? (
                      <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    )}
                    <span className="truncate font-semibold text-gray-900">{group.rawPassName}</span>
                    <span className="text-xs text-gray-500">
                      {group.mappings.length} variant{group.mappings.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    className="shrink-0"
                    onClick={(e) => {
                      e.stopPropagation()
                      setAddRawDefault(group.rawPassName)
                      setAddOpen(true)
                    }}
                  >
                    Add variant
                  </Button>
                </button>

                {isOpen ? (
                  <div className="overflow-x-auto border-t border-gray-100">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-2 font-semibold">Canonical</th>
                          <th className="px-4 py-2 font-semibold">Usage</th>
                          <th className="px-4 py-2 font-semibold">Last used</th>
                          <th className="px-4 py-2 font-semibold">Status</th>
                          <th className="px-4 py-2 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {group.mappings.map((item) => {
                          const isActive = item.id === group.activeMappingId
                          return (
                            <tr key={item.id} data-testid={`pass-mapping-row-${item.id}`}>
                              <td className="px-4 py-2 font-medium text-gray-900">
                                {item.canonicalPassName}
                              </td>
                              <td className="px-4 py-2 text-gray-600">{item.usageCount}</td>
                              <td className="px-4 py-2 text-gray-600">
                                {formatDate(item.lastUsedAt)}
                              </td>
                              <td className="px-4 py-2">
                                {isActive ? (
                                  <span className="inline-flex rounded-full bg-success-50 px-2 py-0.5 text-[10px] font-semibold text-success-700 ring-1 ring-inset ring-success-200">
                                    Active pre-fill
                                  </span>
                                ) : item.isPreferred ? (
                                  <span className="inline-flex rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold text-primary-700">
                                    Preferred
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {!isActive ? (
                                    <Button
                                      type="button"
                                      variant="secondary"
                                      className="!px-2 !py-1 text-xs"
                                      onClick={() => void handlePromote(item)}
                                      data-testid={`pass-mapping-promote-${item.id}`}
                                    >
                                      <StarIcon className="mr-1 inline h-3.5 w-3.5" />
                                      Set preferred
                                    </Button>
                                  ) : null}
                                  <button
                                    type="button"
                                    className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                                    aria-label="Edit mapping"
                                    onClick={() => setEditItem(item)}
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600"
                                    aria-label="Delete mapping"
                                    onClick={() => setDeleteTarget(item)}
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      </div>

      <EditMappingModal
        open={editItem !== null}
        item={editItem}
        onClose={() => setEditItem(null)}
        onSaved={() => void load()}
      />

      <AddMappingModal
        open={addOpen}
        defaultRawPassName={addRawDefault}
        onClose={() => {
          setAddOpen(false)
          setAddRawDefault(undefined)
        }}
        onSaved={() => void load()}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete pass mapping?"
        description={
          deleteTarget
            ? `Remove "${deleteTarget.rawPassName}" → "${deleteTarget.canonicalPassName}" from global history.`
            : ''
        }
        confirmLabel="Delete"
        tone="danger"
        confirmBusy={deleteBusy}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Card>
  )
}
